import { History, Location } from "history";
import { fromQueryString, QueryParams, toQueryString } from "../utils/queryParams";
import { difference, identity, merge, trimEnd } from "lodash";

export type Route<T = void> = {
  /**
   * A name that idientifies the route, which can be used for navigating to the route.
   */
  name: string;
  /**
   *The path template, without any query string, with parameters prefixed with `:`. Example: /foo/:a/:b
   */
  path: string;
  /**
   * A url/path to redirect to. The path can be a template which includes a subset of the parameters
   * of this route. Example:
   *
   * { name: "foo", path: "/foo/:a", redirectTo: "/foo/:a/bar" }
   */
  redirectTo?: string;
  /**
   * Optional route metadata
   */

  data?: T;
};

// The following has been picked up from the following article:
// https://lihautan.com/extract-parameters-type-from-string-literal-types-with-typescript
type PathSegments<T extends string> = T extends `${infer A}/${infer B}`
  ? ParamOnly<A> | PathSegments<B>
  : ParamOnly<T>;
type ParamOnly<T extends string> = T extends `:${infer V}` ? V : never;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type OptionalOnly<T> = T extends `${infer _}?` ? T : never;
type StripOptional<T> = T extends `${infer U}?` ? U : T;
type RequiredOnly<T> = Exclude<T, OptionalOnly<T>>;
type RouteParams<Path extends string> = {
  [Key in RequiredOnly<PathSegments<Path>> as Key]: string;
} & {
  [Key in OptionalOnly<PathSegments<Path>> as StripOptional<Key>]?: string;
};

// Using mapped types to ensure that we get the `path` related to `name`, and not the full union.
// Got this from the EventConfig example in the Typescript handbook: https://www.typescriptlang.org/docs/handbook/2/mapped-types.html
type MatchedRouteIndex<T, R extends Route<T>> = {
  [V in R as V["name"]]: V & { params: RouteParams<V["path"]>; queryParams: QueryParams };
};
export type MatchedRoute<T = void, R extends Route<T> = Route<T>> = MatchedRouteIndex<
  T,
  R
>[keyof MatchedRouteIndex<T, R>];

type AllOptional<T> = { [Key in keyof T]?: T[Key] } extends T ? true : false;
type RouteWithParamsIndex<T, R extends Route<T>> = {
  [V in R as V["name"]]: {
    name: V["name"];
    queryParams?: QueryParams;
  } & (AllOptional<RouteParams<V["path"]>> extends true
    ? { params?: RouteParams<V["path"]> }
    : { params: RouteParams<V["path"]> });
};
export type RouteWithParams<T = void, R extends Route<T> = Route<T>> = RouteWithParamsIndex<
  T,
  R
>[keyof RouteWithParamsIndex<T, R>];

type CompiledRoute = {
  pattern: RegExp;
  paramNames: string[];
};

/**
 * Compiles a route path to a RegExp pattern and extracts the parameter names.
 * Parameters are prefixed with `:` and can be made optional by using `?` as suffix.
 *
 * Examples:
 * - /foo/:x
 * - /foo/:x/:y?/:z?
 */
const compileRoute = <T>(route: Route<T>): CompiledRoute => {
  const tokens = route.path
    .split("/")
    .filter(identity)
    .map((t) => {
      if (t.startsWith(":")) {
        return ["param", trimEnd(t.substring(1), "?"), t.endsWith("?")] as const;
      } else {
        return ["literal", t, false] as const;
      }
    });

  const pattern = new RegExp(
    tokens.reduce((acc, [type, val, optional]) => {
      if (type === "param" && optional) {
        return `${acc}(?:/([^/]+))?`;
      } else if (type === "param") {
        return `${acc}/([^/]+)`;
      } else {
        return `${acc}/${val}`;
      }
    }, "^") + "/?$",
  );

  return {
    pattern,
    paramNames: tokens.filter(([type]) => type === "param").map(([, val]) => val),
  };
};

const extractRouteParams = (
  route: CompiledRoute,
  path: string,
): { [key in string]: string | undefined } => {
  const match = route.pattern.exec(path);
  return Object.fromEntries(route.paramNames.map((p, i) => [p, match?.[i + 1]]));
};

export const interpolateParams = (path: string, params: { [key: string]: unknown } = {}) => {
  return Object.keys(params).reduce(
    (acc, key) => acc.replaceAll(`:${key}`, String(params[key])),
    path,
  );
};

const compiledRouteCache = new Map<string, CompiledRoute>();

export const matchRoute = <T = void, R extends Route<T> = Route<T>>(
  routes: R[],
  location: { pathname: string; search?: string },
) => {
  const match = routes
    .map((x) => {
      if (!compiledRouteCache.has(x.path)) {
        compiledRouteCache.set(x.path, compileRoute(x));
      }
      return [x, compiledRouteCache.get(x.path)!] as const;
    })
    .find(([, compiled]) => compiled.pattern.exec(location.pathname));
  if (match) {
    return {
      ...match[0],
      params: extractRouteParams(match[1], location.pathname),
      queryParams: fromQueryString(location.search ?? ""),
    };
  }
};

export const startRouter = <T, R extends Route<T>>(
  history: History,
  routes: R[],
  onChange: (matchedRoute: MatchedRoute<T, R>) => void,
): void => {
  const handleChange = (location: Location) => {
    const match = matchRoute<T, R>(routes, location);
    if (match?.redirectTo) {
      const targetUrl = interpolateParams(match.redirectTo, match.params);
      const [baseUrl, queryString] = targetUrl.split("?");
      const queryParams = merge(fromQueryString(queryString), match.queryParams);
      history.replace(baseUrl + toQueryString(queryParams));
    } else {
      onChange(match as MatchedRoute<T, R>);
    }
  };
  history.listen(handleChange);
  handleChange(history.location);
};

export const handleLinks = (history: History, hrefPredicate: (href: string) => boolean) => {
  const findLink = (x: Element | HTMLElement | null | undefined) => {
    while (x) {
      if (x.tagName === "A") {
        return x;
      } else {
        x = x.parentElement;
      }
    }
    return null;
  };

  const handleLinkClick = (event: MouseEvent) => {
    const el = findLink(event.target as Element);
    const href = el?.getAttribute("href");
    if (
      href &&
      hrefPredicate(href) &&
      !el?.getAttribute("target") &&
      !event.ctrlKey &&
      !event.altKey &&
      !event.shiftKey &&
      !event.metaKey
    ) {
      event.preventDefault();
      history.push(href);
    }
  };

  document.body.addEventListener("click", handleLinkClick, true);
};

// TODO convert this too
export const createHref = <T = void, R extends Route<T> = Route<T>>({
  routes,
  name,
  params,
  queryParams,
}: {
  routes: R[];
  name: string;
  params?: { [key: string]: unknown };
  queryParams?: { [key: string]: unknown };
}): string => {
  const r = routes.find((r) => r.name === name);
  if (!r) {
    throw new Error(`Route not found: ${name}`);
  }
  const compiled = compiledRouteCache.get(r.name) ?? compileRoute(r);
  const missingParams = difference(compiled.paramNames, Object.keys(params ?? {}));
  if (missingParams.length) {
    throw new Error(`Missing route params for route "${name}": ${missingParams}`);
  }
  return interpolateParams(r.path, params) + toQueryString(queryParams);
};
